import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChatFeedbackButtons } from '~/components/chat-feedback-buttons/ChatFeedbackButtons';
import { ChatQuestionResponse } from '~/components/chat-question-response/ChatQuestionResponse';
import { ErrorBoundary } from '~/components/error-boundary';
import { Message, MessageReply } from '~/stores/use-chat/use-chat.types';
import { AssessmentWidget } from '~/widgets/assessment-widget/AssessmentWidget';
import { AssetOverviewAssistant } from '~/widgets/asset-overview-widget/AssetOverviewAssistant';
import { AssetOverviewWidget } from '~/widgets/asset-overview-widget/AssetOverviewWidget';
import { InsightWidget } from '~/widgets/insight-widget/InsightWidget';
import { PastEpisodesWidget } from '~/widgets/past-episodes-widget/PastEpisodesWidget';
import { ScenarioWidget } from '~/widgets/scenario-widget/ScenarioWidget';

import { AdditionalResponse } from './additional-response/AdditionalResponse';
import { ChartMessage } from './chart-message/ChartMessage';
import { DocumentMessage } from './document-message/DocumentMessage';
import { DominoMessage } from './domino-message/DominoMessage';
import { FallbackMessage } from './fallback-message/FallbackMessage';
import * as S from './Messages.styles';
import { ScreeningMessage } from './screening-message/ScreeningMessage';
import { WatchlistMessage } from './watchlist-message/WatchlistMessage';

export interface MessagesProps {
  messages: Message[];
  sessionId: string;
}

export const Messages = ({ messages, sessionId }: MessagesProps) => {
  const { t } = useTranslation('intel');
  const renderMessage = (primaryReply: MessageReply) => {
    switch (primaryReply?.source) {
      case 'document-assistant': {
        return <DocumentMessage data={primaryReply.data} />;
      }
      case 'watchlist-assistant': {
        return <WatchlistMessage data={primaryReply.data} />;
      }
      case 'chat-quality-score': {
        return <AssessmentWidget {...primaryReply.data} />;
      }
      case 'past-episodes-widget': {
        return <PastEpisodesWidget {...primaryReply.data} />;
      }
      case 'insight-widget': {
        return <InsightWidget {...primaryReply.data} />;
      }
      case 'scenario-widget': {
        return <ScenarioWidget {...primaryReply.data} />;
      }
      case 'asset-overview-widget': {
        return <AssetOverviewWidget {...primaryReply.data} />;
      }
      case 'asset-overview-assistant': {
        return <AssetOverviewAssistant tag={primaryReply.data.tag} />;
      }
      case 'chart-assistant': {
        return <ChartMessage data={primaryReply.data} />;
      }
      case 'screening-assistant': {
        return <ScreeningMessage data={primaryReply.data} />;
      }
      case 'scenario-assistant': {
        return <DominoMessage data={primaryReply.data} />;
      }
      case 'fallback-assistant': {
        return <FallbackMessage data={primaryReply.data} />;
      }
      default: {
        return <p>{t('intel:empty')}</p>;
      }
    }
  };

  return (
    <>
      {messages.map((message, i) => {
        const isQuestionPending = message.status === 'pending';
        const messageAborted = message.status === 'abort';
        const isLastMessage = i === messages.length - 1;
        const [primaryReply, ...restReplies] = message.reply;
        const hasRequestId = primaryReply && 'request_id' in primaryReply;

        return (
          <S.Root
            key={message.id}
            shouldScroll={isLastMessage}
            shouldUpdate={message.status === 'complete'}
          >
            {message.type === 'reset' ? (
              <S.NewSession>
                <span>{t('intel:tryAgain')}</span>
              </S.NewSession>
            ) : (
              <ChatQuestionResponse
                title={message.question}
                isQuestionPending={isQuestionPending}
              >
                {primaryReply ? (
                  <ErrorBoundary fallback={<p>{t('intel:empty')}</p>}>
                    {renderMessage(primaryReply)}
                  </ErrorBoundary>
                ) : !messageAborted ? (
                  <p>{t('intel:empty')}</p>
                ) : null}
                {!!restReplies.length && (
                  <AdditionalResponse
                    replies={restReplies}
                    question={message.question}
                    messageId={message.id}
                  />
                )}
                {messageAborted && (
                  <S.AbortMessage>{t('intel:abortMessage')}</S.AbortMessage>
                )}

                {isLastMessage && hasRequestId && message.type === 'alfred' && (
                  <ChatFeedbackButtons
                    sessionId={sessionId}
                    requestId={primaryReply.request_id}
                    feedback={primaryReply.feedback}
                  />
                )}
              </ChatQuestionResponse>
            )}
          </S.Root>
        );
      })}
    </>
  );
};
